import { ContextMenuAction } from '../../explorer/models/TreeModel';
import type { KeyNodeMetadata } from '../../explorer/models/nodeTypes';
import { getConnectionById } from '../../connections/utils/connectionLookup';
import { toast } from '../../../components/ui/toaster';
import { writeClipboardText } from '../../../lib/clipboard';
import { safeConnectionAlias, safeConnectionServer } from '../../../types/safeConnectionDisplay';

/**
 * Key-specific context menu action types
 */
type KeyContextMenuActionType =
  | 'copy-name'
  | 'script-key-as'
  | 'script-key-create'
  | 'script-key-drop';

const KeyActions = {
  COPY_NAME: 'copy-name' as KeyContextMenuActionType,
  SCRIPT_KEY_AS: 'script-key-as' as KeyContextMenuActionType,
  SCRIPT_KEY_CREATE: 'script-key-create' as KeyContextMenuActionType,
  SCRIPT_KEY_DROP: 'script-key-drop' as KeyContextMenuActionType,
};

/**
 * Generate context menu actions for a key node.
 * SQLite doesn't support ALTER TABLE ADD/DROP CONSTRAINT, so we omit Script as.
 */
export function generateKeyContextMenuActions(engine?: string): ContextMenuAction[] {
  const actions: ContextMenuAction[] = [
    { id: KeyActions.COPY_NAME, label: 'Copy key name', icon: 'clipboard' },
  ];

  if (engine !== 'sqlite') {
    actions.push({
      id: KeyActions.SCRIPT_KEY_AS,
      label: 'Script as',
      icon: 'code',
      submenu: [
        { id: KeyActions.SCRIPT_KEY_CREATE, label: 'CREATE', icon: 'file-plus' },
        { id: KeyActions.SCRIPT_KEY_DROP, label: 'DROP', icon: 'trash' },
      ],
    });
  }

  return actions;
}

/**
 * Generate CREATE constraint DDL for a key
 */
function generateKeyCreateDdl(
  metadata: KeyNodeMetadata,
  quoteId: (name: string) => string,
  qualifyTable: (schema: string, table: string) => string,
): string {
  const fullTable = qualifyTable(metadata.schema, metadata.tableName);
  const cols = metadata.columns.map(c => quoteId(c.name)).join(', ');

  if (metadata.keyType === 'PRIMARY KEY') {
    return `ALTER TABLE ${fullTable}\nADD CONSTRAINT ${quoteId(metadata.keyName)} PRIMARY KEY (${cols});\n`;
  }

  if (metadata.keyType === 'FOREIGN KEY') {
    const refSchema = metadata.columns[0]?.referencedSchema ?? metadata.schema;
    const refTable = metadata.columns[0]?.referencedTable ?? '';
    const refCols = metadata.columns
      .map(c => quoteId(c.referencedColumn ?? c.name))
      .join(', ');
    const refFullTable = qualifyTable(refSchema, refTable);

    return `ALTER TABLE ${fullTable}\nADD CONSTRAINT ${quoteId(metadata.keyName)} FOREIGN KEY (${cols})\n    REFERENCES ${refFullTable} (${refCols});\n`;
  }

  // UNIQUE
  return `ALTER TABLE ${fullTable}\nADD CONSTRAINT ${quoteId(metadata.keyName)} UNIQUE (${cols});\n`;
}

/**
 * Generate DROP constraint DDL for a key (engine-aware)
 */
function generateKeyDropDdl(
  metadata: KeyNodeMetadata,
  engine: string,
  quoteId: (name: string) => string,
  qualifyTable: (schema: string, table: string) => string,
): string {
  const fullTable = qualifyTable(metadata.schema, metadata.tableName);

  if (engine === 'mysql') {
    if (metadata.keyType === 'PRIMARY KEY') {
      return `ALTER TABLE ${fullTable} DROP PRIMARY KEY;\n`;
    }
    if (metadata.keyType === 'FOREIGN KEY') {
      return `ALTER TABLE ${fullTable} DROP FOREIGN KEY ${quoteId(metadata.keyName)};\n`;
    }
    // UNIQUE in MySQL is an index
    return `ALTER TABLE ${fullTable} DROP INDEX ${quoteId(metadata.keyName)};\n`;
  }

  // MSSQL, PostgreSQL, SQLite
  return `ALTER TABLE ${fullTable} DROP CONSTRAINT ${quoteId(metadata.keyName)};\n`;
}

/**
 * Execute a key context menu action
 */
export function executeKeyContextMenuAction(
  actionId: string,
  metadata: KeyNodeMetadata,
): void {
  if (actionId === KeyActions.COPY_NAME) {
    void writeClipboardText(metadata.keyName);
    return;
  }

  if (actionId === KeyActions.SCRIPT_KEY_CREATE || actionId === KeyActions.SCRIPT_KEY_DROP) {
    void handleKeyScript(metadata, actionId);
  }
}

/**
 * Generate key script and open in a new query tab
 */
async function handleKeyScript(
  metadata: KeyNodeMetadata,
  actionId: string,
): Promise<void> {
  const { getScriptProvider } = await import('../../scripting');
  const { useWorkspaceStore } = await import('../../workspace/core/store/workspaceStore');

  const { activeConnection, connection, engine } = getConnectionById(metadata.connectionId);

  if (!activeConnection || !connection) {
    toast.error({ title: 'Error', description: 'Connection not found' });
    return;
  }

  const provider = getScriptProvider(engine);
  const quoteId = (name: string) => provider.quoteIdentifier(name);
  const qualifyTable = (schema: string, table: string) => provider.qualifyTableName(schema, table);

  const isCreate = actionId === KeyActions.SCRIPT_KEY_CREATE;
  const script = isCreate
    ? generateKeyCreateDdl(metadata, quoteId, qualifyTable)
    : generateKeyDropDdl(metadata, engine, quoteId, qualifyTable);

  const { addTab, getNextQueryNumber } = useWorkspaceStore.getState();
  const queryNumber = getNextQueryNumber();
  const titleVerb = isCreate ? 'CREATE' : 'DROP';

  addTab({
    title: `${metadata.keyName}_${titleVerb}-${queryNumber}`,
    type: 'query',
    content: { query: script },
    connectionInfo: {
      connectionId: metadata.connectionId,
      connectionName: safeConnectionAlias(connection, 'Unnamed Connection'),
      serverHost: safeConnectionServer(activeConnection.connection),
      database: metadata.database,
    },
  });
}
